import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import moment from 'moment'
import 'moment/locale/ja'
import { orderBy } from 'lodash'
import Tour from 'components/organisms/Tours/Weekly/Tour'
import * as interfaces from 'static/interfaces'
import * as constants from 'static/constants'
import { MemoModal } from 'components/organisms/Tours/Weekly/Drawer/MemoModal'
import { Tooltip } from 'components/atoms/Tooltip'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow
moment.updateLocale('ja', {
  meridiem(hour, minute, isLowercase) {
    if (hour >= 12) {
      return isLowercase ? 'pm' : 'PM'
    } else {
      return isLowercase ? 'am' : 'AM'
    }
  },
})

interface IProps {
  startDate: moment.Moment
  initialTours: any[]
  noBookingTours: any[]
  currentTours: any[]
  hideAllTourGroup: boolean
  handleClickTour: (tour: any) => void
}

const today = moment()

export const WeeklyTable = ({
  startDate,
  initialTours,
  noBookingTours,
  currentTours,
  hideAllTourGroup,
  handleClickTour,
}: IProps) => {
  const { calendarMemos, setCalendarMemos } = useWeeklyContext()

  const days = React.useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) => {
      const day = startDate.clone().add(index, 'd')
      const calendarKey = day.format('YYYY-MM-DD')
      return {
        date: day.format('M/D(dd)'),
        momentDay: day,
        isToday: day.isSame(today, 'day'),
        calendarMemo: calendarMemos.find(calendarMemo => calendarMemo.day === calendarKey) ?? null,
      }
    })
  }, [calendarMemos, startDate])

  // ツアーの開始時間が30分単位でない時間を抽出
  const getFlexibleStartMoments = React.useCallback(tours => {
    const seen = new Set<string>() // 重複防止
    return tours.reduce((acc, tour) => {
      const tourStartTime = moment(tour.start_time, 'HH:mm')
      const minute = tourStartTime.minute()

      if (minute !== 0 && minute !== 30) {
        const timeStr = tourStartTime.format('HH:mm')
        if (!seen.has(timeStr)) {
          seen.add(timeStr)
          acc.push(tourStartTime)
        }
      }
      return acc
    }, [] as moment.Moment[])
  }, [])

  const startTimes = React.useMemo(() => {
    // ツアーがある一番早い時間と遅い時間の範囲のみ表示する
    const mergedTours = initialTours.concat(noBookingTours)

    const startTime = orderBy(mergedTours, 'start_time')[0]?.start_time || '08:00'
    const endTime = orderBy(mergedTours, 'start_time', 'desc')[0]?.start_time || '17:30'
    const startHour = moment(startTime, 'HH:mm').hour()
    const endHour = moment(endTime, 'HH:mm').hour()

    // 30分単位でない開始時間がある場合の対応
    const flexibleStartMoments = getFlexibleStartMoments(mergedTours)

    const hourIndexes = Array.from({ length: 24 }, (_, index) => index)
    return hourIndexes.reduce((prev, index) => {
      if (startHour > index + 1 || endHour < index) {
        return prev
      }

      const base = [moment({ hour: index }), moment({ hour: index, minute: 30 })]

      flexibleStartMoments.forEach(startMoment => {
        if (startMoment.hour() === index) {
          base.push(startMoment)
        }
      })

      return [...prev, ...base.sort((a, b) => a.diff(b))]
    }, [] as moment.Moment[])
  }, [initialTours, noBookingTours, getFlexibleStartMoments])

  const handleAfterUpdateCalendarMemo = (target, params) => {
    setCalendarMemos(currentMemos => {
      if (params.internal_memo === '') {
        return currentMemos.filter(memo => memo.id !== target.id)
      }

      const isNew = !currentMemos.find(memo => memo.id === target.id)

      if (isNew) {
        return [...currentMemos, target]
      }

      return currentMemos.map(memo => (memo.id === target.id ? target : memo))
    })
  }

  const showDayMemoModal = ({ calendarMemo, momentDay }) => {
    window.globalModal.showModal({
      title: 'カレンダーメモ',
      body: (
        <MemoModal
          target={
            calendarMemo ?? {
              id: null,
              day: momentDay.format('YYYY-MM-DD'),
              internal_memo: '',
              memo: '',
            }
          }
          targetClass="calendarMemo"
          handleAfterUpdate={handleAfterUpdateCalendarMemo}
          internal={true}
        />
      ),
    })
  }

  return (
    <WeeklyTableWrapper id="table">
      <thead id="thead">
        <tr>
          <th scope="col" />
          {days.map((day, index) => (
            <th scope="col" className={`${day.isToday && 'today'}`} key={`day-h-${index}`}>
              <div className="day">
                <span>
                  {day.date}
                  {day.isToday && (
                    <div className="todayIcon">{I18n.t('generic.today', { locale: 'ja' })}</div>
                  )}
                </span>
              </div>
              <div className={`dayMemo${!!day.calendarMemo ? ' hasMemo' : ''}`}>
                <SvgIcon
                  name="note"
                  className="icon"
                  ariaHidden={true}
                  role="button"
                  onClick={() => showDayMemoModal(day)}
                />
                {day.calendarMemo && (
                  <Tooltip content={day.calendarMemo.internal_memo} position="bottom">
                    <p className="memoContent">{day.calendarMemo.internal_memo}</p>
                  </Tooltip>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody id="tbody">
        {startTimes.map((time, index) => (
          <tr key={`start-time-${index}`}>
            <th scope="row">
              <span>{time.format('h:mm A')}</span>
            </th>
            {days.map((day, i) => (
              <td className={`${day.isToday && 'today'}`} key={`day-${i}`}>
                {currentTours
                  .filter(
                    tour =>
                      tour.day === day.momentDay.format('YYYY-MM-DD') &&
                      tour.start_time === time.format('HH:mm')
                  )
                  .map(tour => (
                    <Tour
                      tour={tour}
                      key={tour.key}
                      hideAllTourGroup={hideAllTourGroup}
                      handleClickTour={handleClickTour}
                    />
                  ))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </WeeklyTableWrapper>
  )
}

const WeeklyTableWrapper = styled.table`
  width: 100%;
  font-size: 14px;
  border: 0;
  border: 1px solid ${constants.COLORS.divider};
  thead,
  tbody tr {
    display: table;
    width: 100%;
    table-layout: fixed;
  }
  thead {
    position: relative;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.1);
    z-index: 1;
    th {
      text-align: center;
      background-color: ${constants.COLORS.background};
      vertical-align: top;
      &:first-child {
        width: 84px;
        background-color: ${constants.COLORS.white};
      }
      .day {
        padding: 4px;
        line-height: 2.2rem;
      }
      .dayMemo {
        padding: 4px;
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: 4px;
        color: ${constants.COLORS.subText};
        border-top: 1px solid ${constants.COLORS.divider};
        font-size: 12px;
        text-align: left;
        .icon {
          cursor: pointer;
          flex-shrink: 0;
          width: 16px;
          height: 16px;
        }
        .memoContent {
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          word-break: keep-all;
          overflow-wrap: break-word;
          overflow-wrap: anywhere;
        }
        &.hasMemo {
          color: ${constants.COLORS.black};
          .icon {
            color: ${constants.COLORS.primary};
          }
        }
      }
    }
  }
  tbody {
    position: relative;
    display: block;
    max-height: 72vh;
    font-size: 13px;
    overflow: auto;
    scrollbar-gutter: stable;
    z-index: 0;
    tr {
      &:first-child {
        position: relative;
        margin-top: 24px;
        th,
        td {
          position: relative;
          &:before {
            content: '';
            position: absolute;
            top: -24px;
            left: 0;
            width: 100%;
            height: 24px;
            background: inherit;
            border-right: 1px solid ${constants.COLORS.divider};
            border-bottom: 1px solid ${constants.COLORS.divider};
            box-sizing: content-box;
          }
        }
      }
      &:last-child {
        th,
        td {
          border-bottom: 0;
        }
        td {
          padding-bottom: 52px;
        }
      }
    }
    th {
      width: 84px;
      text-align: right;
      vertical-align: top;
      span {
        position: relative;
        display: block;
        line-height: 20px;
        margin: -12px 4px 0 0;
        padding-right: 4px;
        background-color: ${constants.COLORS.white};
      }
    }
    td {
      height: 40px;
      padding: 1px;
      background-color: ${constants.COLORS.background};
      vertical-align: top;
      > div:last-child {
        margin-bottom: 32px;
      }
    }
  }
  th,
  td {
    padding: 0;
    font-weight: 400;
    border: 0;
    border-right: 1px solid ${constants.COLORS.divider};
    border-bottom: 1px solid ${constants.COLORS.divider};
    &.today {
      background-color: #e5f3f0 !important;
    }
  }
  th.today {
    span {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .todayIcon {
      line-height: 14px;
      margin-left: 4px;
      padding: 4px 8px;
      color: white;
      font-size: 12px;
      font-weight: 700;
      background-color: ${constants.COLORS.primary};
      border-radius: 4px;
      white-space: nowrap;
    }
  }
`
